import React from 'react';
import Icon from './Icon';

/**
 * ErrorBoundary Component
 * 
 * A reusable error boundary that catches JavaScript errors anywhere in the child
 * component tree and displays a fallback UI instead of crashing the entire app.
 * 
 * Features:
 * - Catches runtime errors in child components
 * - Displays user-friendly error message
 * - Shows error details in development mode
 * - Provides option to reload the page
 * - Logs errors to console for debugging
 * 
 * Usage:
 * ```jsx
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 * 
 * Or wrap specific routes:
 * ```jsx
 * <Route path="/admin" element={
 *   <ErrorBoundary>
 *     <AdminDashboard />
 *   </ErrorBoundary>
 * } />
 * ```
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  /**
   * Update state when an error is caught
   */
  static getDerivedStateFromError(error) {
    // Update state so the next render shows fallback UI
    return { hasError: true };
  }

  /**
   * Log error details and update state
   */
  componentDidCatch(error, errorInfo) {
    // Log error to console for debugging
    console.error('🚨 ErrorBoundary caught an error:', error);
    console.error('🔍 Error details:', errorInfo);
    
    // Update state with error information
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // In production, you might want to log to an error reporting service
    // e.g., Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === 'production') {
      // logErrorToService(error, errorInfo);
    }
  }

  /**
   * Reset error boundary state
   */
  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  /**
   * Reload the entire page
   */
  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, errorCount } = this.state;
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            {/* Error Card */}
            <div className="bg-gray-900 rounded-2xl border border-red-500/30 shadow-2xl overflow-hidden">
              
              {/* Header */}
              <div className="bg-gradient-to-r from-red-600 to-red-700 p-6">
                <div className="flex items-center space-x-3">
                  <div className="bg-white/20 rounded-full p-3">
                    <Icon name="alert-circle" size={32} className="text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-white">
                      Oops! Something went wrong
                    </h1>
                    <p className="text-red-100 text-sm">
                      The application encountered an unexpected error
                    </p>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              <div className="p-6 space-y-4">
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <p className="text-red-400 font-semibold mb-2">Error:</p>
                  <p className="text-gray-300 font-mono text-sm">
                    {error?.toString() || 'Unknown error occurred'}
                  </p>
                </div>

                {/* Development Mode: Show Error Details */}
                {isDevelopment && errorInfo && (
                  <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                    <p className="text-gray-400 font-semibold mb-2 flex items-center">
                      <Icon name="info" size={16} className="mr-2" />
                      Stack Trace (Development Mode):
                    </p>
                    <pre className="text-gray-400 font-mono text-xs overflow-auto max-h-64 p-3 bg-gray-900 rounded">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}

                {/* Error Count Warning */}
                {errorCount > 1 && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <p className="text-yellow-400 flex items-center">
                      <Icon name="alert-circle" size={16} className="mr-2" />
                      This error has occurred {errorCount} times
                    </p>
                  </div>
                )}

                {/* What to do */}
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                  <p className="text-white font-semibold mb-2">What can you do?</p>
                  <ul className="text-gray-300 space-y-2 text-sm">
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      Try reloading the page using the button below
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      Clear your browser cache and cookies
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-400 mr-2">•</span>
                      Contact support if the problem persists: support@hopseat.app
                    </li>
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    onClick={this.handleReload}
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg"
                  >
                    <Icon name="refresh" size={20} className="mr-2" />
                    Reload Page
                  </button>
                  
                  <button
                    onClick={() => window.location.href = '/'}
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-600 transition-all duration-300"
                  >
                    <Icon name="home" size={20} className="mr-2" />
                    Go to Home
                  </button>
                </div>

                {/* Development Mode: Reset Button */}
                {isDevelopment && (
                  <button
                    onClick={this.handleReset}
                    className="w-full inline-flex items-center justify-center px-4 py-2 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700 transition-all duration-300 text-sm"
                  >
                    <Icon name="x" size={16} className="mr-2" />
                    Reset Error Boundary (Dev Only)
                  </button>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-900/50 border-t border-gray-800 px-6 py-4">
                <p className="text-sm text-gray-500 text-center">
                  If this issue persists, please contact our support team with the error details above.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
